// Where this copy of the app sends its requests.
//
// The web build has one origin: the server that served the page, which does
// the work *and* owns the account. The desktop build has two, and the split is
// the whole point of it:
//
//   * the local API — a Python server the desktop app starts on this machine,
//     on a loopback port of its own. Uploads, transcription, matching,
//     stitching, waveforms and exports all happen here, against files on this
//     disk. Nothing leaves the machine.
//   * the account API — audiostitcher.in, which still owns sign-in,
//     subscription and anything administrative, because those are not the
//     user's to decide locally.
//
// The desktop shell passes both in at launch (see executables/src-tauri),
// either on `window.__AUDIOSTITCHER__` or as query parameters on the page it
// opens. The web build passes neither and keeps its single origin, so nothing
// here changes how it behaves.

function fromWindow() {
  if (typeof window === 'undefined') return null;
  const cfg = window.__AUDIOSTITCHER__;
  return cfg && typeof cfg === 'object' ? cfg : null;
}

function fromQuery() {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const api = q.get('api');
    if (!api) return null;
    return {
      desktop: true,
      apiBase: api,
      accountBase: q.get('account') || '',
      apiKey: q.get('key') || '',
    };
  } catch {
    return null;
  }
}

// Read once: the shell sets this before the app loads, and a value that could
// change under the app would be worse than one that cannot.
const RUNTIME = fromWindow() || fromQuery() || {};

// Trailing slashes would double up when a path is appended.
const trim = (s) => String(s || '').replace(/\/+$/, '');

export const IS_DESKTOP = !!RUNTIME.desktop;

// Work goes here. Empty string means "same origin as this page", which is what
// the web build wants.
export const WORK_BASE = IS_DESKTOP
  ? trim(RUNTIME.apiBase)
  : (import.meta.env.VITE_WEB_API || '');

// Accounts go here. Only set on desktop; on the web the work base already is
// the account server.
export const ACCOUNT_BASE = IS_DESKTOP ? trim(RUNTIME.accountBase) : '';

// The key the local server expects. It is generated fresh at every launch and
// handed to this page, so another process on the machine cannot drive the
// local API even though it is listening on loopback.
export const LOCAL_API_KEY = IS_DESKTOP ? String(RUNTIME.apiKey || '') : '';

// Everything the account server owns. Anything not listed is local work.
//
// Kept as prefixes rather than exact paths because these routers all have
// sub-paths (`/api/subscription/request`, `/api/admin/users`, …) and a new one
// should follow its router without needing a change here.
const ACCOUNT_PREFIXES = [
  '/api/auth',          // registration, the account-exists check, media tokens
  '/api/me',            // who am I, and what is my plan
  '/api/subscription',  // plan changes and payment confirmations
  '/api/enterprise',    // team roster
  '/api/admin',         // superadmin tools
  '/api/contact',       // the marketing site's form, same server
  '/api/session',       // single-session enforcement, which is account state
];

/** True when this path belongs to the account server rather than local work. */
export function isAccountPath(path) {
  const p = String(path || '');
  return ACCOUNT_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/**
 * The origin a given API path should go to.
 *
 * On the web both answers are the same empty string, so every call stays
 * same-origin exactly as before.
 */
export function baseFor(path) {
  if (IS_DESKTOP && ACCOUNT_BASE && isAccountPath(path)) return ACCOUNT_BASE;
  return WORK_BASE;
}
